// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { format, parseISO } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

interface DateTimePickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
}

export function DateTimePicker({ value, onChange, placeholder = "Pick a date and time" }: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const [dateFormat] = useDateFormat();

  const parsed = value ? parseISO(value) : undefined;
  const timeStr = parsed ? format(parsed, "HH:mm") : "09:00";

  function parseTime(str: string): [number, number] {
    const parts = str.split(":").map(Number);
    return [parts[0] ?? 9, parts[1] ?? 0];
  }

  function handleDateSelect(date: Date | undefined) {
    if (!date) {
      onChange(undefined);
      setOpen(false);
      return;
    }
    const [hours, minutes] = parseTime(timeStr);
    date.setHours(hours, minutes, 0, 0);
    onChange(date.toISOString());
    setOpen(false);
  }

  function handleTimeChange(time: string) {
    if (!parsed) return;
    const [hours, minutes] = parseTime(time);
    const updated = new Date(parsed);
    updated.setHours(hours, minutes, 0, 0);
    onChange(updated.toISOString());
  }

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn("min-w-0 flex-1 justify-start text-left font-normal", !parsed && "text-muted-foreground")}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <span className="truncate">{parsed ? formatDate(parsed, dateFormat) : placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={parsed}
            onSelect={handleDateSelect}
            disabled={(date) => date < tomorrow}
          />
        </PopoverContent>
      </Popover>
      <Input
        type="time"
        className="w-28 shrink-0"
        value={timeStr}
        onChange={(e) => handleTimeChange(e.target.value)}
        disabled={!parsed}
      />
    </div>
  );
}
